/**
 * Patient Authorization Form Component
 *
 * HIPAA-compliant e-signature form for patients to authorize designated agents
 * to access their healthcare information.
 *
 * Implements ESIGN/UETA-compliant electronic signature with:
 * - Typed name signature
 * - Consent to electronic signature
 * - IP address and timestamp capture
 * - Clear display of authorization scope
 */

import { useState } from 'react';
import {
  Shield,
  FileText,
  User,
  Calendar,
  AlertTriangle,
  Check,
  X,
  Info,
  Loader2,
} from 'lucide-react';

export type ConsentType = 'electronic_signature' | 'verbal_consent';

export interface AuthorizationDetails {
  authorizationDocumentId: string;
  caseFileId: string;
  caseFileName: string;
  requestedBy: {
    name: string;
    email: string;
    relationship: string;
  };
  patient: {
    fullName: string;
    email: string;
    dateOfBirth: string;
  };
  scope: {
    description: string;
  };
  purpose: string;
  expiration: {
    type: 'date' | 'event' | 'until_revoked';
    date?: string;
    event?: string;
  };
  createdAt: string;
}

interface PatientAuthorizationFormProps {
  authorization: AuthorizationDetails;
  onApprove: (consentType: ConsentType, consentText: string) => Promise<void>;
  onDeny: () => Promise<void>;
  isSubmitting: boolean;
}

export function PatientAuthorizationForm({
  authorization,
  onApprove,
  onDeny,
  isSubmitting,
}: PatientAuthorizationFormProps): JSX.Element {
  const [typedName, setTypedName] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [showDenyConfirm, setShowDenyConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatExpiration = () => {
    const { expiration } = authorization;
    if (expiration.type === 'date' && expiration.date) {
      return `Expires on ${formatDate(expiration.date)}`;
    }
    if (expiration.type === 'event' && expiration.event) {
      return `Expires when: ${expiration.event}`;
    }
    return 'Valid until revoked by you';
  };

  const canSubmit = typedName.trim().length > 0 && consentChecked && !isSubmitting;

  const handleApprove = async () => {
    if (!canSubmit) return;

    // Validate typed name matches patient name (case-insensitive, trimmed)
    const normalizedTyped = typedName.trim().toLowerCase();
    const normalizedPatient = authorization.patient.fullName.trim().toLowerCase();

    if (normalizedTyped !== normalizedPatient) {
      setError(
        `Please type your full legal name exactly as shown: "${authorization.patient.fullName}"`
      );
      return;
    }

    setError(null);
    await onApprove('electronic_signature', typedName.trim());
  };

  const handleDeny = async () => {
    setShowDenyConfirm(false);
    await onDeny();
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-evergreen" />
        </div>
        <h1 className="text-2xl font-display text-deep-forest mb-2">
          Healthcare Information Authorization Request
        </h1>
        <p className="text-muted">
          Review and respond to this authorization request
        </p>
      </div>

      {/* Authorization Details Card */}
      <div className="card mb-6">
        <div className="card-header">
          <h2 className="font-semibold text-ink flex items-center gap-2">
            <FileText className="w-5 h-5 text-evergreen" />
            Authorization Request Details
          </h2>
        </div>
        <div className="card-body space-y-6">
          {/* Who is requesting */}
          <div>
            <h3 className="text-sm font-semibold text-muted uppercase tracking-wide mb-2">
              Requested By
            </h3>
            <div className="bg-airy rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-brass/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-brass" />
                </div>
                <div>
                  <p className="font-semibold text-ink">{authorization.requestedBy.name}</p>
                  <p className="text-sm text-muted">{authorization.requestedBy.email}</p>
                  <p className="text-sm text-muted">
                    Relationship: <span className="font-medium">{authorization.requestedBy.relationship}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Patient Information */}
          <div>
            <h3 className="text-sm font-semibold text-muted uppercase tracking-wide mb-2">
              Your Information
            </h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-muted">Full Name</dt>
                <dd className="font-medium text-ink">{authorization.patient.fullName}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted">Email</dt>
                <dd className="font-medium text-ink">{authorization.patient.email}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted">Date of Birth</dt>
                <dd className="font-medium text-ink">{formatDate(authorization.patient.dateOfBirth)}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted">CaseFile</dt>
                <dd className="font-medium text-ink">{authorization.caseFileName}</dd>
              </div>
            </dl>
          </div>

          {/* What Information */}
          <div>
            <h3 className="text-sm font-semibold text-muted uppercase tracking-wide mb-2">
              Information to be Shared
            </h3>
            <p className="text-ink bg-amber-50 border border-amber-200 rounded-lg p-4">
              {authorization.scope.description ||
                'All documents, care logs, and notes stored in your CaseFile, including medical records, therapy notes, educational documents, and care coordination information.'}
            </p>
          </div>

          {/* Purpose */}
          <div>
            <h3 className="text-sm font-semibold text-muted uppercase tracking-wide mb-2">
              Purpose
            </h3>
            <p className="text-ink">
              {authorization.purpose ||
                'Care coordination and administrative caregiving support'}
            </p>
          </div>

          {/* Expiration */}
          <div className="flex items-center gap-3 bg-airy rounded-lg p-4">
            <Calendar className="w-5 h-5 text-muted" />
            <span className="text-ink">{formatExpiration()}</span>
          </div>
        </div>
      </div>

      {/* Important Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">Your Rights</p>
            <ul className="list-disc list-inside space-y-1">
              <li>You can revoke this authorization at any time</li>
              <li>Revoking will not affect information already shared</li>
              <li>You will receive a copy of this authorization for your records</li>
              <li>The authorized person cannot further share your information without additional consent</li>
            </ul>
          </div>
        </div>
      </div>

      {/* E-Signature Section */}
      <div className="card mb-6">
        <div className="card-header">
          <h2 className="font-semibold text-ink">Electronic Signature</h2>
          <p className="text-sm text-muted mt-1">
            Sign below to authorize access to your healthcare information
          </p>
        </div>
        <div className="card-body space-y-4">
          {/* Typed Name Input */}
          <div>
            <label htmlFor="typedName" className="label">
              Type Your Full Legal Name <span className="text-red-500">*</span>
            </label>
            <input
              id="typedName"
              type="text"
              value={typedName}
              onChange={(e) => {
                setTypedName(e.target.value);
                setError(null);
              }}
              className={`input ${error ? 'input-error' : ''}`}
              placeholder={`Type "${authorization.patient.fullName}" to sign`}
              disabled={isSubmitting}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'typedName-error' : undefined}
            />
            {error && <p id="typedName-error" className="error-text" role="alert">{error}</p>}
            <p className="helper-text">
              Your typed name serves as your electronic signature
            </p>
          </div>

          {/* Consent Checkbox */}
          <div className="bg-airy rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-soft-neutral text-evergreen focus:ring-evergreen"
                disabled={isSubmitting}
              />
              <span className="text-sm text-ink">
                I understand that by typing my name above and clicking &quot;Authorize Access&quot;,
                I am providing my electronic signature and consent to share my healthcare information
                as described above. This authorization complies with HIPAA and the ESIGN Act.
                I have read and understand this authorization form.
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={handleApprove}
          disabled={!canSubmit}
          className="btn-primary flex-1 flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Authorize Access
            </>
          )}
        </button>

        {!showDenyConfirm ? (
          <button
            onClick={() => setShowDenyConfirm(true)}
            disabled={isSubmitting}
            className="btn-secondary flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" />
            Deny Request
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={handleDeny}
              disabled={isSubmitting}
              className="btn-ghost text-red-600 hover:bg-red-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <AlertTriangle className="w-4 h-4" />
              )}
              Confirm Deny
            </button>
            <button
              onClick={() => setShowDenyConfirm(false)}
              disabled={isSubmitting}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Denial Warning */}
      {showDenyConfirm && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div className="text-sm text-red-800">
              <p className="font-semibold mb-1">Are you sure you want to deny this request?</p>
              <p>
                The person who requested access will be notified that you have denied their
                request. They will not be able to access your healthcare information through
                this platform.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 text-center text-sm text-muted">
        <p>
          Request received on {formatDate(authorization.createdAt)}
        </p>
        <p className="mt-1">
          This authorization is protected by HIPAA privacy regulations
        </p>
      </div>
    </div>
  );
}

export default PatientAuthorizationForm;
